/*
  Decision handlers: who goes where, when they go, and what happens to the capacity they leave behind.

  setBucket is the one to read first. In the single-file app it is "THE single write path for bucket
  assignments" (orgchart.html:3677) and it does three things, only one of which is obvious from the
  name — it writes the bucket, it merges or drops the stage metadata, and it silently deletes the
  person's stranded mitigation plan when they join the carve-out or are made redundant. That last one
  is invisible at every call site, and it is the reason a leaver's RemainCo plan disappears during a
  data refresh. Reproduced here deliberately, and commented where the original was not.
*/
import { DEFAULT_BUCKET, DEFAULT_STRANDED_MONTH } from '@integrate/contracts';
import { freshState } from '../domain.js';
import { previewPerimeterThreshold } from '../analytics.js';
import { CommandError } from '../errors.js';
import { required, } from './types.js';
const EMPTY_DECISION = {
    status: null,
    statusSrc: null,
    joinStage: null,
    day2Exit: null,
    strandedAction: null,
    strandedMonth: null,
};
export function decisionOf(slice, employeeId) {
    return required(slice, 'decisions')[employeeId] ?? EMPTY_DECISION;
}
export function bucketOfSlice(slice, employeeId) {
    return decisionOf(slice, employeeId).status ?? DEFAULT_BUCKET;
}
/**
 * Resolve a named person, or reject: a command must not silently apply to nobody.
 *
 * A planned hire counts. A build-up carries a bucket, a stage attribution and a head designation like
 * anybody else — they are simply a person with no source row in the client's HRIS — so a handler that
 * only consulted the employee table would refuse every decision about the carve-out's own new hires.
 * The build-up slice is read optionally, so a handler that cannot involve one need not load it.
 */
export function personOf(slice, employeeId) {
    const person = required(slice, 'employees')[employeeId];
    if (person)
        return person;
    const buildUp = slice.buildUps?.[employeeId];
    if (buildUp) {
        return {
            id: buildUp.id,
            name: buildUp.name,
            // A planned hire has no dedication: they are a carve-out hire, not RemainCo capacity partly
            // serving it, so they can never carry a stranded mitigation plan. That falls out of this null.
            dedication: null,
            fte: buildUp.fte,
            salary: buildUp.salary,
            openPosition: false,
            inactive: false,
            isLeaver: false,
            levels: buildUp.levels,
            // The marker that says "there is no imported record here", which is what stops an as-is edit
            // being aimed at somebody who was never imported.
            isBuildUp: true,
        };
    }
    throw new CommandError('not_found', `No such employee: ${employeeId}`);
}
/**
 * The decision row a bucket assignment produces, ported from setBucket (orgchart.html:3677).
 *
 * Entering the carve-out records src and joinStage, merging with what is already there so a
 * re-assignment does not silently reset someone's stage; `day2Exit` survives unless explicitly
 * cleared. Leaving the carve-out drops the metadata entirely. And a stranded plan only means anything
 * for a Remain-side person, so joining the carve-out or being made redundant removes it — undecided
 * and Remain keep theirs.
 */
export function bucketPatch(previous, bucket, options = {}) {
    if (bucket === 'Carve-out') {
        // Only an existing member's metadata is merged forward; arriving from Remain starts clean.
        const keep = previous.status === 'Carve-out' ? previous : EMPTY_DECISION;
        /*
          A NAMED STAGE IS A DELIBERATE STAGE DECISION, AND A DELIBERATE STAGE DECISION IS A MANUAL ONE.
    
          The frozen app converts on either direction of the toggle — `{ ...meta, joinStage: js, src:
          'manual' }` (orgchart.html:5100), with the person panel saying so out loud at `:5089`: "changing
          the stage makes the assignment manual (the slider will no longer move this person)". `setJoinStage`
          already ports that. `setBucket` and `setBucketBulk` did not, and they are the two commands that
          accept a stage as part of the assignment — so the single natural instruction, "put these people in
          the carve-out as Day 1 transfers", left a rule pick rule-owned with a Day 1 stage.
    
          That state is not cosmetic. `previewPerimeterThreshold` classifies a rule-owned member by dedication
          ALONE — the `isRule` branch never looks at `joinStage` (analytics.ts) — so the next slider drag past
          their dedication puts them in `exitIds` and deletes the decision row, transfer and all. The
          TransfersPanel avoids it by sending `src: 'manual'` and then a second `setJoinStage`; a model or an
          API caller issuing the one obvious command got the trap instead.
    
          Only when the caller names NO source: an explicit `src` still wins, which is what keeps
          `applyPerimeterThreshold`'s own `{ src: 'rule', joinStage: 'perimeter' }` writes rule-owned, and
          what lets an inverse put a rule pick back exactly as it was. Every inverse in this file names both
          or neither, because a Carve-out row always carries a source — `bucketPatch` writes one on every
          path and the importer backfills `'manual'` for a file that omitted it.
        */
        const stageWasNamed = options.joinStage !== undefined;
        return {
            status: 'Carve-out',
            statusSrc: options.src ?? (stageWasNamed ? 'manual' : keep.statusSrc ?? 'manual'),
            // The original defaults this from state.phase, a per-user lens; here absent means 'perimeter'.
            joinStage: options.joinStage ?? keep.joinStage ?? 'perimeter',
            day2Exit: options.day2Exit === true || (!!keep.day2Exit && options.day2Exit !== false),
            strandedAction: null,
            strandedMonth: null,
        };
    }
    // Leaving the carve-out drops the stage metadata entirely, src included — nothing reads the source
    // of a Remain or No-longer-required decision, and keeping it would imply the rule could own one.
    const dropsPlan = bucket === 'No longer required';
    return {
        status: bucket,
        statusSrc: null,
        joinStage: null,
        day2Exit: null,
        ...(dropsPlan ? { strandedAction: null, strandedMonth: null } : {}),
    };
}
/**
 * A row with nothing set at all is not worth storing.
 *
 * An explicit `Remain` IS something set, and used to be collapsed here on the grounds that "the absent
 * form reads identically". It does not, and the difference is the whole safety property of the perimeter
 * slider. Both the original and the ported rule engine read the RAW entry, so:
 *
 *   · absent  → UNDECIDED, which the rule is free to sweep into the carve-out;
 *   · 'Remain' → a human's explicit decision, counted as manualOut and never moved.
 *
 * Collapsing the second into the first meant a consultant who hand-excluded somebody from the carve-out
 * had that decision silently reversed by the next threshold apply — or by the rule re-run inside a roster
 * refresh, which is how this surfaced. The original stores it (setBucket writes state.buckets
 * unconditionally, orgchart.html:3691) and reads presence with hasOwn, so storing it is also the faithful
 * behaviour rather than an addition.
 *
 * A `null` status is still empty: that is a genuine clear, and a rule exit uses it precisely so the person
 * returns to being rule-eligible rather than being frozen as an explicit Remain.
 */
function isEmptyPatch(previous, patch) {
    const merged = { ...previous, ...patch };
    return (merged.status === null &&
        merged.joinStage === null &&
        !merged.day2Exit &&
        merged.strandedAction === null);
}
export function decisionWrite(scenarioId, employeeId, previous, patch) {
    return {
        t: 'decision',
        scenarioId,
        employeeId,
        patch: isEmptyPatch(previous, patch) ? null : patch,
    };
}
/**
 * The commands that put this person's decision row back exactly as it was.
 *
 * Two of them when a stranded plan was dropped. setBucket removes the mitigation plan of anyone
 * joining the carve-out or made redundant, so restoring the bucket alone would leave the plan gone —
 * an undo that silently loses data is worse than no undo. Only the fields that were actually set are
 * named, so the inverse says "there was no decision" rather than asserting a default.
 */
function restoreBucket(scenarioId, employeeId, previous) {
    const restore = [
        {
            type: 'setBucket',
            scenarioId,
            employeeId,
            bucket: previous.status,
            ...(previous.statusSrc ? { src: previous.statusSrc } : {}),
            ...(previous.joinStage ? { joinStage: previous.joinStage } : {}),
            ...(previous.day2Exit ? { day2Exit: true } : {}),
        },
    ];
    if (previous.strandedAction !== null) {
        restore.push({
            type: 'setStrandedPlan',
            scenarioId,
            assignments: [
                {
                    employeeId,
                    action: previous.strandedAction,
                    month: previous.strandedMonth ?? 0,
                },
            ],
        });
    }
    return restore;
}
function assertScenario(ctx, commandScenarioId) {
    if (commandScenarioId !== ctx.scenarioId) {
        throw new CommandError('conflict', 'Command targets a different scenario than the session');
    }
}
/**
 * A planned hire is Remain or Carve-out, never No-longer-required.
 *
 * The original refuses this outright ("Build-ups can only be Remain or Carve-out",
 * orgchart.html:5374) and it is not a UI nicety. `computeOneOffCosts` splits build-ups on
 * `bucket === 'Carve-out'` and puts everything else in the RemainCo bucket
 * (packages/core/src/economics.ts:198), so a position marked No-longer-required is drawn as cut and
 * priced as a RemainCo hire at the same time — its full hiring cost lands in `remainHiring` while the
 * chart shows it removed. A number nobody can reconcile, from a state the app cannot produce.
 *
 * Deleting the position is the way to un-plan a hire, and it is one click away in the same panel.
 */
function assertBucketable(person, bucket) {
    if (!person.isBuildUp || bucket !== 'No longer required')
        return;
    throw new CommandError('conflict', `${person.name} is a planned hire — Remain or Carve-out only. Delete the position instead.`);
}
/* ------------------------------------------------------------------ */
export const setBucket = {
    needs: ['employees', 'buildUps', 'decisions'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const person = personOf(slice, command.employeeId);
        assertBucketable(person, command.bucket);
        const previous = decisionOf(slice, command.employeeId);
        const before = previous.status ?? DEFAULT_BUCKET;
        const after = command.bucket ?? DEFAULT_BUCKET;
        const patch = bucketPatch(previous, command.bucket, {
            src: command.src,
            joinStage: command.joinStage,
            day2Exit: command.day2Exit,
        });
        return {
            writes: [decisionWrite(command.scenarioId, command.employeeId, previous, patch)],
            inverse: restoreBucket(command.scenarioId, command.employeeId, previous),
            summary: before === after
                ? `${person.name} unchanged (${after})`
                : `${person.name}: ${before} → ${after}`,
            touched: [`bucket:${command.employeeId}`],
        };
    },
};
export const setBucketBulk = {
    needs: ['employees', 'buildUps', 'decisions'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const writes = [];
        const touched = [];
        /*
          The inverse is TWO commands, whatever N is.
    
          It used to be one `setBucket` per person — plus a second for anyone whose stranded plan the move
          dropped — so a 500-person move stored 500 to 1000 commands. `commandRequestSchema` caps a client
          batch at 200, so the one undo route the client can reach could not replay its own inverse; only
          `RevertService`, which sets `serverGenerated`, could, and even then it meant N seq bumps, N
          `change_events` rows and N realtime frames for one undo click.
    
          The objection that forced per-person is answered rather than overruled: an assignment now carries
          its own `src`, `joinStage` and `day2Exit`, so one bulk restore CAN give each person back exactly
          what they had. Absent still means "had none" — the field is omitted, not defaulted — which is the
          distinction `restoreBucket` preserves and the reason a naive bulk restore was wrong before.
        */
        const restoreBuckets = [];
        const restorePlans = [];
        // Both ends of the move, per bucket, so the log entry says what changed rather than only where to.
        const tally = new Map();
        for (const assignment of command.assignments) {
            assertBucketable(personOf(slice, assignment.employeeId), assignment.bucket);
            const previous = decisionOf(slice, assignment.employeeId);
            /*
              Per-assignment metadata wins; the command-level `src` is the fallback.
      
              `src` stays OPTIONAL at both levels: omitting it carries each person's stored source forward,
              which is what a group bulk means — reassigning a team says nothing about who decided that one of
              them was already a rule pick. The per-assignment form is what lets a mixed selection send
              `src: 'manual'` for its manual picks and nothing for its rule picks in ONE command.
            */
            const patch = bucketPatch(previous, assignment.bucket, {
                src: assignment.src ?? command.src,
                joinStage: assignment.joinStage,
                day2Exit: assignment.day2Exit,
            });
            writes.push(decisionWrite(command.scenarioId, assignment.employeeId, previous, patch));
            touched.push(`bucket:${assignment.employeeId}`);
            restoreBuckets.push({
                employeeId: assignment.employeeId,
                bucket: previous.status,
                ...(previous.statusSrc ? { src: previous.statusSrc } : {}),
                ...(previous.joinStage ? { joinStage: previous.joinStage } : {}),
                ...(previous.day2Exit ? { day2Exit: true } : {}),
            });
            if (previous.strandedAction !== null) {
                restorePlans.push({
                    employeeId: assignment.employeeId,
                    action: previous.strandedAction,
                    month: previous.strandedMonth ?? 0,
                });
            }
            const from = previous.status ?? 'undecided';
            const to = assignment.bucket ?? DEFAULT_BUCKET;
            const key = `${from} → ${to}`;
            tally.set(key, (tally.get(key) ?? 0) + 1);
        }
        const inverse = [
            { type: 'setBucketBulk', scenarioId: command.scenarioId, assignments: restoreBuckets },
        ];
        if (restorePlans.length) {
            inverse.push({ type: 'setStrandedPlan', scenarioId: command.scenarioId, assignments: restorePlans });
        }
        /*
          The summary names both ends.
    
          It used to tally destinations only — "Moved 500 people (500 → Carve-out)" — so a reviewer reading
          the log six months later could not tell what those people had been, which on an M&A redundancy
          trail is the entry that matters most. The FROM values were already in hand, on their way into the
          inverse.
        */
        const parts = [...tally].map(([move, n]) => `${n} ${move}`).join(', ');
        return {
            writes,
            inverse,
            summary: `Moved ${command.assignments.length} ${command.assignments.length === 1 ? 'person' : 'people'} (${parts})`,
            touched,
        };
    },
};
export const setJoinStage = {
    needs: ['employees', 'buildUps', 'decisions'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const writes = [];
        const touched = [];
        const restore = [];
        const restorePlans = [];
        for (const assignment of command.assignments) {
            // Named, not numbered. This was the one refusal in the file quoting a raw id — and it is the one
            // most likely to be hit by a cross-chart selection, which is heterogeneous by construction, so it
            // reported an employee id the user has never seen for a person they can see on screen.
            const person = personOf(slice, assignment.employeeId);
            const previous = decisionOf(slice, assignment.employeeId);
            // Stage attribution only means anything for a carve-out member.
            if (previous.status !== 'Carve-out') {
                throw new CommandError('conflict', `${person.name} is not in the carve-out, so has no join stage`);
            }
            writes.push(decisionWrite(command.scenarioId, assignment.employeeId, previous, {
                joinStage: assignment.joinStage,
                /*
                  A deliberate stage decision takes the person away from the rule (orgchart.html:5100, and the
                  person panel says so at :5089 — "changing the stage makes the assignment manual").
      
                  This is not cosmetic. previewPerimeterThreshold classifies a rule-owned member by dedication
                  alone and ignores their join stage, so a rule pick promoted to a Day 1 transfer would (a) be
                  counted in the perimeter rather than in transfers, disagreeing with computeStageBridge, and
                  (b) be swept out entirely — decision row deleted, transfer lost — the moment somebody raised
                  the threshold past their dedication. Someone dragging a slider would silently destroy a Day 1
                  decision they never saw.
                */
                statusSrc: 'manual',
            }));
            touched.push(`joinStage:${assignment.employeeId}`);
            /*
              The whole previous row, not just the stage: taking ownership from the rule is part of what this
              command did, so putting the stage back has to put the ownership back with it.
      
              Collected rather than pushed per person. This used to emit one `setBucket` per assignment — the
              same O(N) inverse `setBucketBulk` had, and reachable the same way, since "set Day 1 for these 300
              transfers" is exactly what a selection toolbar offers. One widened `setBucketBulk` carries each
              person's own src, stage and Day 2 flag, which is what "the whole previous row" means.
            */
            restore.push({
                employeeId: assignment.employeeId,
                bucket: previous.status,
                ...(previous.statusSrc ? { src: previous.statusSrc } : {}),
                ...(previous.joinStage ? { joinStage: previous.joinStage } : {}),
                ...(previous.day2Exit ? { day2Exit: true } : {}),
            });
            if (previous.strandedAction !== null) {
                restorePlans.push({
                    employeeId: assignment.employeeId,
                    action: previous.strandedAction,
                    month: previous.strandedMonth ?? 0,
                });
            }
        }
        const inverse = [
            { type: 'setBucketBulk', scenarioId: command.scenarioId, assignments: restore },
        ];
        if (restorePlans.length) {
            inverse.push({ type: 'setStrandedPlan', scenarioId: command.scenarioId, assignments: restorePlans });
        }
        const n = command.assignments.length;
        const label = command.assignments[0].joinStage === 'day1' ? 'Day 1' : 'Perimeter';
        return {
            writes,
            inverse,
            summary: n === 1
                ? `${personOf(slice, command.assignments[0].employeeId).name} joins at ${label}`
                : `${label} for ${n} people`,
            touched,
        };
    },
};
export const setDay2Exit = {
    needs: ['employees', 'buildUps', 'decisions'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const writes = [];
        const touched = [];
        const restore = [];
        for (const assignment of command.assignments) {
            const person = personOf(slice, assignment.employeeId);
            /*
              A planned hire's Day 2 exit lives on the build-up row, not on the decision row.
      
              Every reader honours that split — `waveOf`, `stageIncludesId` and the Day 2 bridge all check
              `p.isBuildUp` first — so writing the flag here produced a 201, an event reading "X exits at Day
              2", and no change to any headcount, rightsizing cost or stage lens. It also left
              `scenario_employee_decision.day2_exit` permanently disagreeing with `build_ups.day2_exit` for
              the same person. Both client call sites already branch and send `updateBuildUp` instead; this
              makes the model say so rather than leaving the invariant to two React components.
            */
            if (person.isBuildUp) {
                throw new CommandError('conflict', `${person.name} is a planned hire — set the Day 2 exit on the position itself`);
            }
            const previous = decisionOf(slice, assignment.employeeId);
            /*
              AND THE SAME GUARD `setJoinStage` MAKES, for the same reason and one command later.
      
              A Day 2 exit is read exclusively off `bucketMeta`, which `loadScenarioDesign` projects ONLY for
              rows whose status is 'Carve-out' (apps/server/src/state.ts) — the frozen app's own invariant,
              where the meta record exists only for members (`:3676`) and the checkbox is only rendered inside
              the carve-out branch of the person panel (`:5086`). So flagging a Remain or undecided person
              returned a 201, wrote a decision row, emitted an event reading "X exits at Day 2", and changed
              nothing any reader would ever see: not the rightsizing cost, not the Day 2 headcount, not the
              stage lens.
      
              That is the same silent no-op the `isBuildUp` guard above was added to close, one condition
              short — and this file already refuses it by name for the join stage, which made the pair
              asymmetric in the direction that hides.
            */
            if (previous.status !== 'Carve-out') {
                throw new CommandError('conflict', `${person.name} is not in the carve-out, so has no Day 2 exit`);
            }
            writes.push(decisionWrite(command.scenarioId, assignment.employeeId, previous, {
                day2Exit: assignment.day2Exit,
            }));
            touched.push(`day2Exit:${assignment.employeeId}`);
            restore.push({ employeeId: assignment.employeeId, day2Exit: !!previous.day2Exit });
        }
        const n = command.assignments.length;
        const on = command.assignments[0].day2Exit;
        return {
            writes,
            inverse: [{ type: 'setDay2Exit', scenarioId: command.scenarioId, assignments: restore }],
            summary: n === 1
                ? `${personOf(slice, command.assignments[0].employeeId).name} ${on ? 'exits' : 'stays'} at Day 2`
                : `${on ? 'Exits' : 'Stays'} at Day 2 for ${n} people`,
            touched,
        };
    },
};
/** The first few reasons in full, then a count — a summary column is not the place for five hundred. */
function namesOf(reasons) {
    const shown = reasons.slice(0, 3);
    const rest = reasons.length - shown.length;
    return shown.join('; ') + (rest > 0 ? `, and ${rest} more` : '');
}
/**
 * Why this person cannot carry a stranded plan, or null if they can.
 *
 * One predicate rather than four inline throws, because it is now asked in two moods: a forward command
 * REFUSES on it and a replayed inverse SKIPS on it. Two copies would drift, and the drift would be
 * invisible — an undo that restores a plan the forward path would have rejected produces an entry every
 * read path then ignores, which is the state these conditions exist to prevent.
 *
 * The sentence is the message either way, so a skip can say the same thing a refusal would have.
 */
function ineligibleReason(person, previous) {
    if (person.isBuildUp) {
        return `${person.name} is a planned hire, not RemainCo capacity — nothing to strand`;
    }
    if (person.isLeaver) {
        return `${person.name} has left the organisation, so carries no stranded capacity`;
    }
    const bucket = previous.status ?? DEFAULT_BUCKET;
    if (bucket !== DEFAULT_BUCKET) {
        return `${person.name} is ${bucket}, so carries no stranded capacity to mitigate`;
    }
    if (person.dedication == null || person.dedication <= 0) {
        return `${person.name} has no dedication recorded, so carries no stranded capacity`;
    }
    return null;
}
export const setStrandedPlan = {
    needs: ['employees', 'buildUps', 'decisions'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const writes = [];
        const touched = [];
        const restore = [];
        // Tallied by what was actually asked for, so a mixed batch can say so.
        const tally = new Map();
        let unchanged = 0;
        const skipped = [];
        for (const assignment of command.assignments) {
            const person = personOf(slice, assignment.employeeId);
            const previous = decisionOf(slice, assignment.employeeId);
            /*
              A stranded plan mitigates capacity left behind in RemainCo, so it only means anything for a
              real, staying, partly-dedicated person — which is exactly `strandedEligible`, the predicate
              every read path already applies. The guards below are that predicate, enforced where the write
              happens rather than left to the readers: a command the engine accepts and every consumer then
              ignores is worse than a refusal, because the panel badges it back as if it took.
      
              Clearing (`action: null`) is never refused. An entry can exist on somebody who has since become
              ineligible — imported files carry them — and there has to be a way to remove it.
      
              A REPLAY skips instead of refusing, and the difference is the whole point of `ctx.replaying`.
              `setBucketBulk` records one of these for every plan its move dropped, so undoing a five-hundred
              person move replays five hundred restores against the project as it stands NOW — and a refusal
              is atomic, so one person whose dedication was blanked by a roster refresh in between takes the
              entire undo down with a message naming somebody the user was not thinking about. The supersede
              guard cannot see it coming either: it compares `touched` keys, and `employee:<id>` does not
              overlap `bucket:<id>` or `stranded:<id>`.
      
              Skipping is also the honest answer, not merely the convenient one. The stored plan was valid
              when it was recorded and the person is no longer eligible to carry one, so restoring it would
              put back an entry every read path ignores — the exact state these refusals exist to prevent.
              Reported below rather than swallowed, because a partial restore the log does not mention is the
              silent-data-loss shape this codebase keeps closing.
            */
            if (assignment.action !== null) {
                const ineligible = ineligibleReason(person, previous);
                if (ineligible !== null) {
                    if (!ctx.replaying)
                        throw new CommandError('conflict', ineligible);
                    skipped.push(ineligible);
                    continue;
                }
            }
            /*
              A no-op is not a change.
      
              Re-selecting the action somebody already has, or clearing a plan on somebody who never had one,
              used to write a row, bump the sequence and record "Cleared the stranded plan" in the history.
              This history is the audit trail for a redundancy decision; entries that record nothing having
              happened are not cosmetic noise in it. setBucket in this file already says "X unchanged".
            */
            /*
              ABSENT MEANS "KEEP THEIRS, ELSE SIX" — which is what the RemainCo panel has always sent, in the
              one line of the client that knew: `prev ? prev.month : 6`, ported from orgchart.html:6441.
      
              The schema said `.default(0)`, "effective immediately", so the platform's stated default and its
              only real caller disagreed and anything else driving the command — a proposal, a script, the
              assistant — got the wrong one. Resolved HERE rather than by moving the 6 into the default,
              because a fixed default is wrong in the more common direction: re-selecting the action on a plan
              that already says month 12 would silently move it, and nobody asked for that. The panel no longer
              carries its own copy.
            */
            const month = assignment.month ?? previous.strandedMonth ?? DEFAULT_STRANDED_MONTH;
            const sameAction = previous.strandedAction === assignment.action;
            const sameMonth = assignment.action === null || (previous.strandedMonth ?? 0) === month;
            if (sameAction && sameMonth) {
                unchanged++;
                continue;
            }
            writes.push(decisionWrite(command.scenarioId, assignment.employeeId, previous, {
                strandedAction: assignment.action,
                strandedMonth: assignment.action === null ? null : month,
            }));
            touched.push(`stranded:${assignment.employeeId}`);
            restore.push({
                employeeId: assignment.employeeId,
                action: previous.strandedAction,
                month: previous.strandedMonth ?? 0,
            });
            const label = assignment.action === null
                ? 'Cleared'
                : `${assignment.action === 'rightsize' ? 'Rightsize' : 'Redeploy'} M${month}`;
            tally.set(label, (tally.get(label) ?? 0) + 1);
        }
        /*
          A skipped restore is stated, never implied by a smaller number.
    
          A replay that put back 498 of 500 plans and said "498 people" would be a partial restore the log
          does not mention — the reader has no way to learn that two are missing or who. The names go in the
          summary, capped, because this is the audit trail for a redundancy decision and "somebody" is not
          an answer anybody can act on.
        */
        const skipNote = skipped.length ? ` — ${skipped.length} not restored: ${namesOf(skipped)}` : '';
        const first = command.assignments[0];
        if (!writes.length) {
            return {
                writes: [],
                /*
                  An EMPTY inverse, not a null one. `null` says "this can never be undone", which is a sentence
                  about deleting a scenario, not about a command that changed nothing.
        
                  Two things went wrong while this was null. The change log would have called a no-op
                  irreversible, in the same words as a roster refresh. And a batch is refused outright if ANY of
                  its events has a null inverse (apps/server/src/revert.ts), so one already-correct person in a
                  twenty-person stranded batch made the whole batch unrevertable — exactly the defect `addNote`
                  was changed to avoid. Every other no-op path in the catalogue already returns `[]`.
                */
                inverse: [],
                summary: skipped.length
                    ? `Stranded plans could not be restored: ${namesOf(skipped)}`
                    : `${personOf(slice, first.employeeId).name}: stranded plan unchanged`,
                touched: [],
            };
        }
        /*
          The summary tallies every distinct outcome rather than describing the first one.
    
          A batch that rightsizes twelve people at month 6 and redeploys eight at month 12 used to read
          "Rightsize from month 6 for 20 people" — a sentence about a decision nobody made. setBucketBulk
          already tallies; this now matches it.
        */
        const parts = [...tally].map(([label, n]) => (n === 1 ? label : `${label} × ${n}`)).join(', ');
        const moved = writes.length;
        return {
            writes,
            inverse: [{ type: 'setStrandedPlan', scenarioId: command.scenarioId, assignments: restore }],
            summary: moved === 1 && !unchanged && !skipped.length
                ? `${personOf(slice, restore[0].employeeId).name}: ${parts}`
                : `${parts} (${moved} ${moved === 1 ? 'person' : 'people'}${unchanged ? `, ${unchanged} unchanged` : ''})${skipNote}`,
            touched,
        };
    },
};
/* ------------------------------------------------------------------ *
 * The perimeter rule
 * ------------------------------------------------------------------ */
/**
 * Build the minimal SeparationState previewPerimeterThreshold reads.
 *
 * Reusing that function rather than re-deriving the rule is the point: it encodes which decisions the
 * slider is allowed to move — undecided people and its own previous picks, never a human's explicit
 * choice — and the parity harness holds it to the shipped app across every scenario. A second
 * implementation here would be a second thing to keep true.
 */
function stateForThreshold(slice) {
    return thresholdState(required(slice, 'employees'), required(slice, 'decisions'));
}
/**
 * The same state, from an explicit roster and decision set.
 *
 * Exported because a roster refresh has to resolve the rule for EVERY scenario, against the roster as it
 * will be AFTER the update — neither of which is what the slice holds. Passing both in keeps one
 * implementation of the rule rather than a second one for the refresh.
 */
export function thresholdState(employees, decisions) {
    const state = freshState();
    state.employees = Object.values(employees).map((e) => ({
        id: e.id,
        name: e.name,
        supervisorId: null,
        jobTitle: null,
        location: null,
        country: null,
        currency: null,
        pensionScheme: null,
        seniority: null,
        businessUnit: null,
        legalEntity: null,
        salary: e.salary,
        fte: e.fte,
        dedication: e.dedication,
        sharedMarker: false,
        openPosition: e.openPosition,
        inactive: e.inactive,
        isLeaver: e.isLeaver,
        isBuildUp: false,
        levels: {},
    }));
    for (const [employeeId, row] of Object.entries(decisions)) {
        if (row.status)
            state.design.buckets[employeeId] = row.status;
        if (row.status === 'Carve-out') {
            state.design.bucketMeta[employeeId] = {
                src: row.statusSrc ?? 'manual',
                joinStage: row.joinStage ?? 'perimeter',
                ...(row.day2Exit ? { day2Exit: true } : {}),
            };
        }
    }
    return state;
}
/** The moves a threshold implies, as decision writes plus the inverse that undoes them. */
function thresholdWrites(slice, scenarioId, threshold) {
    const preview = previewPerimeterThreshold(stateForThreshold(slice), threshold);
    const writes = [];
    const touched = [];
    const restore = [];
    for (const employeeId of preview.enterIds) {
        const previous = decisionOf(slice, employeeId);
        writes.push(decisionWrite(scenarioId, employeeId, previous, {
            ...bucketPatch(previous, 'Carve-out', { src: 'rule', joinStage: 'perimeter' }),
        }));
        touched.push(`bucket:${employeeId}`);
        restore.push({ employeeId, previous });
    }
    for (const employeeId of preview.exitIds) {
        const previous = decisionOf(slice, employeeId);
        writes.push(decisionWrite(scenarioId, employeeId, previous, bucketPatch(previous, null)));
        touched.push(`bucket:${employeeId}`);
        restore.push({ employeeId, previous });
    }
    return {
        writes,
        touched,
        restore,
        entered: preview.enterIds.length,
        exited: preview.exitIds.length,
    };
}
/**
 * Undo a threshold change: put the stored rule back, then put every decision it moved back.
 *
 * Both halves are needed and the ORDER matters. Restoring the rule alone leaves the people it moved
 * where it moved them; restoring the people alone leaves the scenario advertising a rule nobody is
 * governed by, so the next dedication edit re-sweeps them. Applying the old rule first re-runs it
 * against the current roster — which is fine, because the per-person restores that follow overwrite
 * whatever it decided, and each command in a batch observes the ones before it.
 *
 * The restores go in ONE `setBucketBulk`, and the reason that is safe is worth stating, because it was
 * not always true. A bulk restore used to carry one `src` for the whole batch, which `bucketPatch`
 * preferred over each person's stored value — so restoring a rule sweep stamped 'manual' on everybody the
 * rule owned and the slider stopped governing them for good; and it could carry neither `joinStage` nor
 * `day2Exit`, so a restored Day 1 transfer came back as a perimeter member. `setBucketBulk` now takes all
 * three PER ASSIGNMENT, so one command says exactly what each person had. A dropped stranded plan rides
 * along in a second command.
 *
 * That matters at scale as well as for correctness: a slider drag across a large roster stored an inverse
 * of one command per person, which the client's own batch ceiling then refused to replay.
 */
function thresholdInverse(scenarioId, previousThreshold, restore) {
    /*
      The rule VALUE, not a re-run of the rule.
  
      This used to emit `applyPerimeterThreshold` / `clearPerimeterThreshold`, which materialise — and an
      inverse re-executes as a command against PRESENT state, so putting the old rule back re-swept the
      roster as it stands at undo time. Anyone the old rule newly reaches — somebody whose dedication a
      data refresh raised, or who was moved back to Remain since the apply and therefore collapsed to an
      undecided row, back inside the rule's reach — took a Carve-out designation, which drops their
      stranded mitigation plan. The per-person restores below could not cover them: they were computed
      from what the FORWARD command touched. The plan was gone, with no error, and the history said only
      that a threshold had been undone.
  
      Restoring the value alone leaves those restores as the sole authority on who is where.
    */
    const rule = { type: 'setPerimeterRule', scenarioId, threshold: previousThreshold };
    const inverse = [rule];
    if (restore.length) {
        inverse.push({
            type: 'setBucketBulk',
            scenarioId,
            assignments: restore.map((r) => ({
                employeeId: r.employeeId,
                bucket: r.previous.status,
                // Omitted, not defaulted: absent means "had none", which is the distinction that makes a bulk
                // restore faithful rather than a reset to whatever the defaults happen to be.
                ...(r.previous.statusSrc ? { src: r.previous.statusSrc } : {}),
                ...(r.previous.joinStage ? { joinStage: r.previous.joinStage } : {}),
                ...(r.previous.day2Exit ? { day2Exit: true } : {}),
            })),
        });
        const plans = restore
            .filter((r) => r.previous.strandedAction !== null)
            .map((r) => ({
            employeeId: r.employeeId,
            action: r.previous.strandedAction,
            month: r.previous.strandedMonth ?? 0,
        }));
        if (plans.length)
            inverse.push({ type: 'setStrandedPlan', scenarioId, assignments: plans });
    }
    return inverse;
}
/**
 * Record the stored perimeter rule without re-running it.
 *
 * The only path that writes `perimeterThreshold` and no memberships, which is what makes it a usable
 * inverse — see `thresholdInverse` above. It is also a real thing to want on its own: agreeing the
 * rule before agreeing who it moves. The summary says memberships did not move, because a stored
 * threshold that disagrees with the buckets is exactly what a reader would otherwise assume was applied.
 */
export const setPerimeterRule = {
    needs: ['scenario'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const scenario = required(slice, 'scenario');
        const pct = (t) => (t == null ? 'no rule' : `${Math.round(t * 100)}%`);
        return {
            writes: [
                {
                    t: 'scenarioPatch',
                    scenarioId: command.scenarioId,
                    patch: { perimeterThreshold: command.threshold },
                },
            ],
            inverse: [
                {
                    type: 'setPerimeterRule',
                    scenarioId: command.scenarioId,
                    threshold: scenario.perimeterThreshold,
                },
            ],
            summary: command.threshold == null
                ? 'Removed the stored perimeter rule — memberships unchanged'
                : `Perimeter rule recorded at ${pct(command.threshold)} dedication — memberships unchanged` +
                    (scenario.perimeterThreshold == null ? '' : ` (was ${pct(scenario.perimeterThreshold)})`),
            touched: [`threshold:${command.scenarioId}`],
        };
    },
};
/**
 * The governance gate on the perimeter — the SteerCo's freeze, spoken through the owner.
 *
 * The handler only flips the flag; the ENFORCEMENT lives in the server's one write path, beside the
 * archived-project gate it copies, so proposals-apply, reverts, websocket and HTTP are all covered
 * without knowing about it. Refusing the no-op keeps the history honest: "froze the perimeter"
 * twice in a row is one act and one lie.
 */
export const setPerimeterFrozen = {
    needs: ['scenario'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const scenario = required(slice, 'scenario');
        if (scenario.perimeterFrozen === command.frozen) {
            throw new CommandError('conflict', command.frozen
                ? 'The perimeter of this scenario is already frozen'
                : 'The perimeter of this scenario is not frozen');
        }
        return {
            writes: [
                {
                    t: 'scenarioPatch',
                    scenarioId: command.scenarioId,
                    patch: { perimeterFrozen: command.frozen },
                },
            ],
            inverse: [
                { type: 'setPerimeterFrozen', scenarioId: command.scenarioId, frozen: scenario.perimeterFrozen },
            ],
            summary: command.frozen
                ? 'Froze the perimeter — direct changes now need an applied proposal'
                : 'Unfroze the perimeter — direct changes allowed again',
            touched: [`perimeterFrozen:${command.scenarioId}`],
        };
    },
};
export const applyPerimeterThreshold = {
    // No build-ups: the rule classifies the imported roster against a dedication threshold, and a planned
    // hire has no dedication to classify.
    needs: ['employees', 'decisions', 'scenario'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const scenario = required(slice, 'scenario');
        const moves = thresholdWrites(slice, command.scenarioId, command.threshold);
        return {
            writes: [
                ...moves.writes,
                {
                    t: 'scenarioPatch',
                    scenarioId: command.scenarioId,
                    patch: { perimeterThreshold: command.threshold },
                },
            ],
            inverse: thresholdInverse(command.scenarioId, scenario.perimeterThreshold, moves.restore),
            summary: `Perimeter rule at ${Math.round(command.threshold * 100)}% dedication` +
                (moves.entered || moves.exited ? ` (+${moves.entered} in, −${moves.exited} out)` : ' (no change)') +
                (scenario.perimeterThreshold == null ? '' : ` — was ${Math.round(scenario.perimeterThreshold * 100)}%`),
            touched: [...moves.touched, `threshold:${command.scenarioId}`],
        };
    },
};
export const clearPerimeterThreshold = {
    needs: ['employees', 'decisions', 'scenario'],
    apply(slice, command, ctx) {
        assertScenario(ctx, command.scenarioId);
        const scenario = required(slice, 'scenario');
        // Clearing the rule releases everyone it had picked, which is what threshold=null resolves to.
        const moves = thresholdWrites(slice, command.scenarioId, null);
        return {
            writes: [
                ...moves.writes,
                { t: 'scenarioPatch', scenarioId: command.scenarioId, patch: { perimeterThreshold: null } },
            ],
            /*
              Re-applying the old threshold alone is not the inverse. It re-picks whoever qualifies, but it
              stamps every one of them `joinStage: 'perimeter'` and drops their stranded plan — so a released
              Day 1 transfer comes back as a perimeter member, and a mitigation plan the clear removed stays
              removed. The per-person restores say what was actually there.
            */
            /*
              EMPTY when there was no rule and nobody was released, not null. That is the no-op case, and `null`
              is the answer to a different question — it says the change can never be undone, which is a sentence
              about deleting a scenario. It also made any batch containing this clear unrevertable outright, the
              same way a no-op stranded plan did before P4m-c.
            */
            inverse: scenario.perimeterThreshold == null && moves.restore.length === 0
                ? []
                : thresholdInverse(command.scenarioId, scenario.perimeterThreshold, moves.restore),
            summary: `Cleared the perimeter rule${moves.exited ? ` (−${moves.exited} out)` : ''}`,
            touched: [...moves.touched, `threshold:${command.scenarioId}`],
        };
    },
};
//# sourceMappingURL=decisions.js.map